import { z } from '@faceup/validation'
import { DisclosureFormStatus, DisclosureSubmissionStatus } from '../enums'
import { LONG_STRING_MAX_LENGTH, MAX_SELECT_FORM_ITEM_OPTIONS_COUNT } from '../validationConfig'
import { baseFormItemSchema, formItemOptionSchema } from './abstractFormItemSchemas'
import { memberSchema } from './modelSchemas'
import { allowedDisclosureFormItemTypes } from './publicDisclosureFormSchema'

export const disclosureShortLinkSchema = z.string().regex(/^[a-z0-9]{8}$/)

/**
 * Wire counterpart of the backend `AF_AnswerInputType`, narrowed to what disclosure forms can contain
 * (see `allowedDisclosureFormItemTypes`): no `files` and no `matrixValues`.
 */
export const disclosureAnswerInputSchema = z.object({
  formItemId: z.uuidv7(),
  // also carries the free-text value of a Select/MultiSelect "other" option
  textValue: z.string().max(LONG_STRING_MAX_LENGTH).nullish(),
  optionIds: z.array(z.uuidv7()).max(MAX_SELECT_FORM_ITEM_OPTIONS_COUNT).nullish(),
})

// Uncapped: forms have no item limit, and validateAnswers rejects unknown and duplicate ids anyway
export const disclosureSubmissionInputSchema = z.object({
  answers: z.array(disclosureAnswerInputSchema).min(1),
})

export type DisclosureAnswerInput = z.infer<typeof disclosureAnswerInputSchema>

/**
 * Question copy only (no answer-input details like options or input constraints), narrowed to
 * disclosure form item types.
 */
const submissionDisclosureFormItemSchema = baseFormItemSchema.extend({
  type: z.enum(allowedDisclosureFormItemTypes),
})

export type SubmissionDisclosureFormItem = z.infer<typeof submissionDisclosureFormItemSchema>

/**
 * One question of a submitted form, paired with its given answer. `formItem` is question copy
 * only (no answer-input details, e.g. options); `selectedOptions` is empty for text answers.
 */
export const disclosureSubmissionAnswerSchema = z.object({
  formItem: submissionDisclosureFormItemSchema,
  textValue: z.string().nullable(),
  selectedOptions: z.array(formItemOptionSchema),
})

export const disclosureSubmissionDetailSchema = z.object({
  id: z.uuidv7(),
  disclosureFormId: z.uuidv7(),
  disclosureFormName: z.string().nullable(),
  disclosureFormStatus: z.enum(DisclosureFormStatus),
  /**
   * `null` on an invite sent to a bare address: the name only becomes known once the invitee
   * answers, or right away when the address matched an employee row.
   */
  submitterName: z.string().nullable(),
  submitterEmail: z.string(),
  /** `null` while the invite is still waiting for its response. */
  submittedAt: z.iso.datetime().nullable(),
  status: z.enum(DisclosureSubmissionStatus),
  assignees: z.array(memberSchema),
  /** Empty until the invitee answers — the detail shows "awaiting response" instead. */
  answers: z.array(disclosureSubmissionAnswerSchema),
})

export type DisclosureSubmissionDetail = z.infer<typeof disclosureSubmissionDetailSchema>
